<template>
    <div class="progress-wrap" :class="{'compact': onlyTooltip}">
        <ul class="background-bar">
            <span class="first-bar" :style="{width: halfCssWidth +'%'}"></span>
            <li v-for="(state, index) in stateSequence" :class="getLiClasses(state, index)"
                :style="{width: cssWidth +'%'}">
                <span class="status-circle" v-tooltip="state.getLabel()">
                    <span class="status-index">{{index+1}}</span>
                </span>
                <span class="progress-text" :class="{'d-none': onlyTooltip}">| {{state.getLabel()}}</span>
                <span class="progress-bar" :style="{width: getCorrectWidth(index) +'%'}"></span>
            </li>
        </ul>
    </div>
</template>

<script>
  import StatusProgressState from "#/js/components/StatusProgressState";

  export default {
    name: "StatusProgressBar",
    props: {
      current: String,
      states: {type: Array, default: ['default1', 'default2', 'default1']},
      processing: {type: Boolean, default: false,},
      onlyTooltip: {type: Boolean, default: false,},
    },
    computed: {
      stateSequence() {
        return this.states.map((value) => {
          if (value instanceof StatusProgressState) {
            return value;
          }
          return new StatusProgressState(value);
        })
      },
      cssWidth() {
        return (100 / this.states.length);
      },
      halfCssWidth() {
        return (50 / this.states.length);
      },
      currentStateIndex() {
        return this.stateSequence.findIndex(state => state.getKey() === this.current);
      },
    },
    methods: {
      getCorrectWidth(index) {
        if (index === this.states.length - 1) {
          return 50;
        }
        if (index === this.currentStateIndex) {
          return 0;
        }
        return 100;
      },

      getLiClasses(state, index) {

        const indexOfCurrentState = this.currentStateIndex;
        const finalState = this.states.length - 1;

        const thisIsStateBeforeCurrent = indexOfCurrentState - 1 === index;
        const thisIsStateAfterCurrent = indexOfCurrentState + 1 === index;
        const thisStateIsCompleted = indexOfCurrentState > index || (index === finalState && indexOfCurrentState === index);
        const shouldHaveProcessingBar = this.processing && !(index === finalState);
        const isFirstState = index === 0;
        const shouldHaveActiveBar = indexOfCurrentState === index && !(index === finalState);

        return {
          previous: thisIsStateBeforeCurrent,
          completed: thisStateIsCompleted,
          processing: shouldHaveProcessingBar,
          first: isFirstState,
          next: thisIsStateAfterCurrent,
          active: shouldHaveActiveBar,
        }
      },
    }
  }
</script>

<style lang="scss">
    $circleSize: 20px;
    $barHeight: calc(#{$circleSize} / 3);
    $progressTextTopCorrection: 0px;
    $progressTextLeftCorrection: 50%;
    $beforeTopCorrection: -23px;
    $beforeLeftCorrection: 50%;
    $beforeMarginLeftCorrection: calc(-#{$circleSize} / 2);
    $afterTopCorrection: -17px;

    /*Text formatting*/
    $circleFontWeight: 700;
    $progressTextFontWeight: 500;
    $activeFontWeight: 700;
    $letterSpacing: 0.03rem;
    $circleFontSize: calc(#{$circleSize} / 2);
    $progressTextFontSize: calc(#{$circleSize} / 2);
    /*Colors*/
    $unReachedBarColor: #3a3a3a;
    $unReachedTextColor: #FFF;
    $reachedColor: #ff990d;
    $currentColor: #ff990d;
    $stripeColor: rgba(255, 153, 13, 0.51);
    $borderColor: #ff990d;

    /* animation */
    $progressBarFromWidth: 45%;
    $progressBarToWidth: 55%;

    @mixin not-reached {
        background-color: $unReachedBarColor;
    }

    @mixin reached {
        background-color: $reachedColor;
    }

    @keyframes processing {
        from {
            width: $progressBarFromWidth;
        }
        to {
            width: $progressBarToWidth;
        }
    }

    .progress-text {
        top: $progressTextTopCorrection;
        font-size: $progressTextFontSize;
        left: $progressTextLeftCorrection;
        position: absolute;
        letter-spacing: 0.05rem;
        font-weight: $progressTextFontWeight;
        text-transform: capitalize;
        width: 100%;
    }

    .progress-wrap {
        color: #444;
        font-family: 'PT Sans Caption', sans-serif;
        max-width: 1920px;
        position: relative;
        width: 100%;

        &.compact {
            margin: 20px auto;
        }
    }


    ul.background-bar {
        li {
            color: $unReachedTextColor;
            display: block;
            font-size: 16px;
            font-weight: $progressTextFontWeight;
            letter-spacing: $letterSpacing;
            padding: 14px 20px 14px 80px;
            position: relative;

            & .status-circle {
                background: $unReachedBarColor;
                border: 1px solid $borderColor;
                border-radius: 50%;
                color: $unReachedTextColor;
                font-size: $circleFontSize;
                font-weight: $circleFontWeight;
                letter-spacing: $letterSpacing;
                //left: 20px;
                line-height: 37px;
                height: $circleSize;
                position: absolute;
                text-align: center;
                text-shadow: 1px 1px rgba(0, 0, 0, 0.2);
                top: $beforeTopCorrection;
                width: $circleSize;
                z-index: 999;
                margin-left: $beforeMarginLeftCorrection;
                user-select: none;
                box-shadow: 0 0 1px #ff990d;

                &.has-tooltip:hover {
                    box-shadow: 0 0 0 1px $borderColor;
                }
            }

            &.active {
                color: $currentColor;
                font-weight: $activeFontWeight;
                letter-spacing: $letterSpacing;


                .status-circle {
                    background: $unReachedBarColor;
                    z-index: 99999;
                    color: $currentColor;
                    box-shadow: 0 0 0 1px $borderColor;
                }
            }

            &.completed {
                background: $unReachedBarColor;
                color: $reachedColor;
                z-index: 99999;

                & .status-circle {
                    background: $unReachedBarColor;
                    z-index: 99999;
                    color: $currentColor;
                }
            }
        }
    }

    @media all {
        .background-bar li.active .progress-bar {
            content: "";
            height: $barHeight;
            width: 0;
            transition: none;
            left: 50%;
            position: absolute;
            top: $afterTopCorrection;
            z-index: 0;
        }

        li.active.processing .progress-bar {

            background: repeating-linear-gradient(
                            45deg,
                            $unReachedBarColor,
                            $unReachedBarColor 15%,
                            $reachedColor 15%,
                            $reachedColor 25%
            );
            background-size: 35px 35px;
            animation: processing 1.5s ease-in-out infinite alternate;
        }

        .progress-wrap {
            margin: 80px auto;
        }

        ul.background-bar {

            @include not-reached;
            border-radius: 15px;
            height: $barHeight;
            margin: 0 auto;
            padding: 0;
            position: relative;
            width: 100%;

            & span.first-bar {
                @include reached;
                border-radius: 15px;
                content: " ";
                height: $barHeight;
                left: 0;
                position: absolute;
                width: 10%;
            }

            li {
                display: inline-block;
                margin: 0 0 0;
                padding: 0;
                text-align: left;
                width: 20%;

                & .status-circle {
                    height: $circleSize;
                    left: $beforeLeftCorrection;
                    line-height: $circleSize;
                    position: absolute;
                    top: $beforeTopCorrection;
                    width: $circleSize;
                    z-index: 99;
                    margin-left: $beforeMarginLeftCorrection;
                }

                &.completed {
                    background: none;

                    &:last-child {
                        & .progress-bar {
                            @include reached;
                            content: "";
                            height: $barHeight;
                            left: 50%;
                            position: absolute;
                            top: $afterTopCorrection;
                            z-index: 99;
                            border-radius: 15px;
                        }
                    }

                    & .progress-bar {
                        @include reached;
                        content: "";
                        height: $barHeight;
                        left: 50%;
                        position: absolute;
                        top: $afterTopCorrection;
                        width: 100%;
                        z-index: 99;
                    }
                }
            }
        }
    }
</style>